import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { trackListingCreated } from '../utils/analytics';
import Footer from '../components/Footer';
import { getStates, getCitiesForState } from '../utils/locationData';

const ListItems = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    itemName: '',
    listingType: 'rent', // Default to rent
    itemType: '',
    category: '',
    clothingType: '',
    jewelleryType: '',
    handycraftType: '',
    color: '',
    size: '',
    jewellerySize: '',
    price: '',
    condition: '',
    state: '',
    city: '',
    fabric: '',
    material: '',
    metalPurity: '',
    description: ''
  });
  const [availableStates] = useState(getStates());
  const [availableCities, setAvailableCities] = useState([]);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userStatus, setUserStatus] = useState(null);

  useEffect(() => {
    loadUserStatus();
  }, []);

  const loadUserStatus = async () => {
    try {
      const response = await api.get('/listings/check-limit');
      setUserStatus(response.data);
    } catch (error) {
      // Fallback to membership status if check-limit fails
      try {
        const fallbackResponse = await api.get('/membership/status');
        setUserStatus(fallbackResponse.data);
      } catch (fallbackError) {
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'state') {
      // When state changes, update available cities and clear city selection
      setFormData({
        ...formData,
        state: value,
        city: '' // Clear city when state changes
      });
      setAvailableCities(getCitiesForState(value));
    } else if (name === 'itemType') {
      // When itemType changes, clear category if handycraft is selected
      const updatedFormData = {
        ...formData,
        [name]: value
      };
      
      // Clear category for handycraft items
      if (value === 'handycraft') {
        updatedFormData.category = '';
      }
      
      setFormData(updatedFormData);
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const totalImages = images.length + files.length;

    if (totalImages > 3) {
      toast.error('You can upload maximum 3 images per listing');
      return;
    }

    // Validate file types
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const invalidFiles = files.filter(file => !validTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      toast.error('Please upload only image files (JPG, PNG, GIF, WEBP)');
      return;
    }

    // Validate file sizes (max 5MB per image)
    const invalidSizeFiles = files.filter(file => file.size > 5 * 1024 * 1024);
    if (invalidSizeFiles.length > 0) {
      toast.error('Each image must be less than 5MB');
      return;
    }

    const newImages = [...images, ...files];
    setImages(newImages);

    // Create previews for all images
    const newPreviews = [...imagePreviews];
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push(e.target.result);
        if (newPreviews.length === newImages.length) {
          setImagePreviews(newPreviews);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    setImages(newImages);
    setImagePreviews(newPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (images.length === 0) {
      toast.error('Please upload at least one image');
      return;
    }

    if (!formData.state || !formData.city) {
      toast.error('Please select both state and city');
      return;
    }

    // Check if user can create listing
    if (userStatus && !userStatus.canCreateListing) {
      toast.error('You have reached your listing limit! Purchase membership to get 5 additional listings.', {
        autoClose: 5000
      });
      // Redirect to membership page after showing error
      setTimeout(() => {
        navigate('/membership');
      }, 2000);
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();

      // Add form fields
      Object.keys(formData).forEach(key => {
        if (key === 'state' || key === 'city') {
          // Combine state and city into location field for backend
          if (key === 'city' && formData.state && formData.city) {
            formDataToSend.append('location', `${formData.city}, ${formData.state}`);
          }
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add images
      images.forEach(image => {
        formDataToSend.append('images', image);
      });

      const response = await api.post('/listings', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data) {
        toast.success('Item listed successfully and is now live!');

        // Track successful listing creation
        trackListingCreated(
          formData.itemType,
          formData.category || 'handycraft', // Use 'handycraft' as category for handycraft items
          formData.price
        );

        navigate('/dashboard');
      }
    } catch (error) {

      if (error.response?.status === 403 && error.response?.data?.action === 'upgrade') {
        // Show upgrade message and redirect to membership
        toast.error(error.response.data.upgradeMessage, { autoClose: 5000 });
        setTimeout(() => {
          navigate('/membership');
        }, 2000);
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Error creating listing';
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const canCreateListing = () => {
    if (!userStatus) return true;
    return userStatus.canCreateListing;
  };

  const getStatusMessage = () => {
    if (!userStatus) return 'Loading...';
    return userStatus.message;
  };

  const getStatusColor = () => {
    if (!userStatus) return '#393E46';
    return userStatus.canCreateListing ? '#948979' : '#393E46';
  };

  const getStatusIcon = () => {
    if (!userStatus) return 'fas fa-circle-notch fa-spin';
    return userStatus.canCreateListing ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle';
  };

  return (
    <>
      <Helmet>
        <title>List Your Fashion Items for Rent | Earn Money from Your Wardrobe | VestrioZ</title>
        <meta
          name="description"
          content="List your designer clothes, jewellery & handcrafts for rent on VestrioZ. Earn ₹5,000-₹50,000/month from your wardrobe. Free listing, verified renters, secure payments."
        />
        <meta name="keywords" content="list fashion items for rent, earn from wardrobe, rent out clothes online, list designer clothes, jewellery rental business, handcraft rental, make money from fashion, VestrioZ seller" />
        <link rel="canonical" href="https://vestrioz.com/list-items" />
        <meta property="og:title" content="List Your Fashion Items for Rent | Earn Money from Your Wardrobe" />
        <meta property="og:description" content="Earn ₹5,000-₹50,000/month by renting out your designer clothes, jewellery & handcrafts on VestrioZ." />
        <meta property="og:url" content="https://vestrioz.com/list-items" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="min-h-screen" style={{ backgroundColor: '#F6F1EB' }}>
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#222831' }}>
              List Your Item
            </h1>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#393E46' }}>
              Share your wardrobe and jewellery collection - rent or sell your items
            </p>
          </div>

          {/* Listing Status */}
          <div className={`rounded-2xl p-6 mb-8 shadow-lg border ${userStatus?.canCreateListing ? 'border-green-200/50' : 'border-red-200/50'
            }`} style={{ backgroundColor: 'white' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 ${userStatus?.canCreateListing ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                  <i className={`${getStatusIcon()} ${userStatus?.canCreateListing ? 'text-green-600' : 'text-red-600'
                    } text-xl`}></i>
                </div>
                <div>
                  <p className="font-semibold mb-1" style={{ color: '#222831' }}>Your Listing Status</p>
                  <p style={{ color: getStatusColor() }}>{getStatusMessage()}</p>
                  {userStatus && (
                    <div className="text-sm mt-1" style={{ color: '#393E46' }}>
                      Free: {userStatus.freeListingsRemaining || 0}/{userStatus.freeListingsLimit || 3} •
                      Membership: {userStatus.membershipListingsRemaining || 0} remaining
                    </div>
                  )}
                </div>
              </div>
              {userStatus && !userStatus.canCreateListing && (
                <button
                  onClick={() => navigate('/membership')}
                  className="text-white px-4 py-2 rounded-lg transition-all duration-300 text-sm font-medium"
                  style={{ backgroundColor: '#948979' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#948979'}
                >
                  <i className="fas fa-crown mr-2"></i>
                  Upgrade Now
                </button>
              )}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="rounded-2xl shadow-2xl p-8 border" style={{ backgroundColor: 'white', borderColor: '#948979' }}>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Item Name */}
              <div className="md:col-span-2">
                <label htmlFor="itemName" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  Item Name *
                </label>
                <input
                  type="text"
                  id="itemName"
                  name="itemName"
                  required
                  value={formData.itemName}
                  onChange={handleInputChange}
                  className="form-input w-full"
                  placeholder="e.g., Designer Lehenga, Diamond Necklace, Formal Suit"
                />
              </div>

              {/* Listing Type - Rent or Sell */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  Listing Type *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, listingType: 'rent' })}
                    className={`p-4 rounded-lg border-2 transition-all duration-300 ${formData.listingType === 'rent'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-300 hover:border-gray-400'
                      }`}
                  >
                    <div className="flex items-center justify-center mb-2">
                      <i className={`fas fa-handshake text-2xl ${formData.listingType === 'rent' ? 'text-green-600' : 'text-gray-400'
                        }`}></i>
                    </div>
                    <div className="text-center">
                      <h4 className={`font-semibold ${formData.listingType === 'rent' ? 'text-green-700' : 'text-gray-700'
                        }`}>Rent</h4>
                      <p className="text-xs text-gray-500 mt-1">Lend your item for a daily fee</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, listingType: 'sell' })}
                    className={`p-4 rounded-lg border-2 transition-all duration-300 ${formData.listingType === 'sell'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                      }`}
                  >
                    <div className="flex items-center justify-center mb-2">
                      <i className={`fas fa-tag text-2xl ${formData.listingType === 'sell' ? 'text-blue-600' : 'text-gray-400'
                        }`}></i>
                    </div>
                    <div className="text-center">
                      <h4 className={`font-semibold ${formData.listingType === 'sell' ? 'text-blue-700' : 'text-gray-700'
                        }`}>Sell</h4>
                      <p className="text-xs text-gray-500 mt-1">Sell your item permanently</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Item Type */}
              <div>
                <label htmlFor="itemType" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  Item Type *
                </label>
                <select
                  id="itemType"
                  name="itemType"
                  required
                  value={formData.itemType}
                  onChange={handleInputChange}
                  className="form-input w-full"
                >
                  <option value="">Select Item Type</option>
                  <option value="clothing">Clothing</option>
                  <option value="jewellery">Jewellery</option>
                  <option value="handycraft">Handy Craft</option>
                </select>
              </div>

              {/* Category - Only show for clothing and jewellery */}
              {(formData.itemType === 'clothing' || formData.itemType === 'jewellery') && (
                <div>
                  <label htmlFor="category" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Category *
                  </label>
                  <select
                    id="category"
                    name="category"
                    required
                    value={formData.category}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Category</option>
                    <option value="men">Men</option>
                    <option value="women">Women</option>
                    <option value="unisex">Unisex</option>
                  </select>
                </div>
              )}

              {/* Clothing Type - Only show for clothing */}
              {formData.itemType === 'clothing' && (
                <div>
                  <label htmlFor="clothingType" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Clothing Type *
                  </label>
                  <select
                    id="clothingType"
                    name="clothingType"
                    required
                    value={formData.clothingType}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Type</option>
                    <option value="ethnic">Ethnic</option>
                    <option value="western">Western</option>
                    <option value="casual">Casual</option>
                    <option value="formal">Formal</option>
                    <option value="party">Party</option>
                    <option value="traditional">Traditional</option>
                  </select>
                </div>
              )}

              {/* Jewellery Type - Only show for jewellery */}
              {formData.itemType === 'jewellery' && (
                <div>
                  <label htmlFor="jewelleryType" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Jewellery Type *
                  </label>
                  <select
                    id="jewelleryType"
                    name="jewelleryType"
                    required
                    value={formData.jewelleryType}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Type</option>
                    <option value="necklace">Necklace</option>
                    <option value="earrings">Earrings</option>
                    <option value="bracelet">Bracelet</option>
                    <option value="ring">Ring</option>
                    <option value="anklet">Anklet</option>
                    <option value="bangles">Bangles</option>
                    <option value="chain">Chain</option>
                    <option value="pendant">Pendant</option>
                    <option value="brooch">Brooch</option>
                    <option value="cufflinks">Cufflinks</option>
                    <option value="watch">Watch</option>
                  </select>
                </div>
              )}

              {/* Handy Craft Type - Only show for handycraft */}
              {formData.itemType === 'handycraft' && (
                <div>
                  <label htmlFor="handycraftType" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Handy Craft Type *
                  </label>
                  <select
                    id="handycraftType"
                    name="handycraftType"
                    required
                    value={formData.handycraftType}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Type</option>
                    <option value="pottery">Pottery</option>
                    <option value="woodwork">Woodwork</option>
                    <option value="textiles">Textiles</option>
                    <option value="paintings">Paintings</option>
                    <option value="sculptures">Sculptures</option>
                    <option value="candles">Candles</option>
                    <option value="soaps">Handmade Soaps</option>
                    <option value="bags">Handmade Bags</option>
                    <option value="decorative">Decorative Items</option>
                    <option value="toys">Handmade Toys</option>
                    <option value="accessories">Craft Accessories</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              )}

              {/* Color */}
              <div>
                <label htmlFor="color" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  Color *
                </label>
                <input
                  type="text"
                  id="color"
                  name="color"
                  required
                  value={formData.color}
                  onChange={handleInputChange}
                  className="form-input w-full"
                  placeholder="e.g., Red, Blue, Multi-color"
                />
              </div>

              {/* Size - Only show for clothing */}
              {formData.itemType === 'clothing' && (
                <div>
                  <label htmlFor="size" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Size *
                  </label>
                  <select
                    id="size"
                    name="size"
                    required
                    value={formData.size}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Size</option>
                    <option value="XS">XS</option>
                    <option value="S">S</option>
                    <option value="M">M</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                    <option value="XXL">XXL</option>
                    <option value="28">28</option>
                    <option value="30">30</option>
                    <option value="32">32</option>
                    <option value="34">34</option>
                    <option value="36">36</option>
                    <option value="38">38</option>
                    <option value="40">40</option>
                    <option value="42">42</option>
                  </select>
                </div>
              )}

              {/* Jewellery Size - Only show for certain jewellery types */}
              {formData.itemType === 'jewellery' && ['ring', 'bracelet', 'anklet', 'bangles'].includes(formData.jewelleryType) && (
                <div>
                  <label htmlFor="jewellerySize" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Size *
                  </label>
                  <select
                    id="jewellerySize"
                    name="jewellerySize"
                    required
                    value={formData.jewellerySize}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Size</option>
                    <option value="XS">XS</option>
                    <option value="S">S</option>
                    <option value="M">M</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                    <option value="Free Size">Free Size</option>
                    {formData.jewelleryType === 'ring' && (
                      <>
                        <option value="5">5</option>
                        <option value="6">6</option>
                        <option value="7">7</option>
                        <option value="8">8</option>
                        <option value="9">9</option>
                        <option value="10">10</option>
                        <option value="11">11</option>
                        <option value="12">12</option>
                      </>
                    )}
                  </select>
                </div>
              )}

              {/* Price */}
              <div>
                <label htmlFor="price" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  {formData.listingType === 'rent' ? 'Rental Price (₹/day) *' : 'Sale Price (₹) *'}
                </label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  required
                  min="1"
                  value={formData.price}
                  onChange={handleInputChange}
                  className="form-input w-full"
                  placeholder={formData.listingType === 'rent' ? 'Enter price per day' : 'Enter sale price'}
                />
              </div>

              {/* Condition */}
              <div>
                <label htmlFor="condition" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  Condition *
                </label>
                <select
                  id="condition"
                  name="condition"
                  required
                  value={formData.condition}
                  onChange={handleInputChange}
                  className="form-input w-full"
                >
                  <option value="">Select Condition</option>
                  <option value="excellent">Excellent - Like new</option>
                  <option value="good">Good - Minor wear</option>
                  <option value="fair">Fair - Visible wear but functional</option>
                </select>
              </div>

              {/* State */}
              <div>
                <label htmlFor="state" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  State *
                </label>
                <select
                  id="state"
                  name="state"
                  required
                  value={formData.state}
                  onChange={handleInputChange}
                  className="form-input w-full"
                >
                  <option value="">Select State</option>
                  {availableStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              {/* City */}
              <div>
                <label htmlFor="city" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  City *
                </label>
                <select
                  id="city"
                  name="city"
                  required
                  value={formData.city}
                  onChange={handleInputChange}
                  className="form-input w-full"
                  disabled={!formData.state}
                >
                  <option value="">
                    {formData.state ? 'Select City' : 'Select State First'}
                  </option>
                  {availableCities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              {/* Fabric - Only show for clothing */}
              {formData.itemType === 'clothing' && (
                <div>
                  <label htmlFor="fabric" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Fabric *
                  </label>
                  <input
                    type="text"
                    id="fabric"
                    name="fabric"
                    required
                    value={formData.fabric}
                    onChange={handleInputChange}
                    className="form-input w-full"
                    placeholder="e.g., Cotton, Silk, Polyester"
                  />
                </div>
              )}

              {/* Material - Only show for jewellery */}
              {formData.itemType === 'jewellery' && (
                <div>
                  <label htmlFor="material" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Material *
                  </label>
                  <select
                    id="material"
                    name="material"
                    required
                    value={formData.material}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Material</option>
                    <option value="gold">Gold</option>
                    <option value="silver">Silver</option>
                    <option value="platinum">Platinum</option>
                    <option value="diamond">Diamond</option>
                    <option value="pearl">Pearl</option>
                    <option value="gemstone">Gemstone</option>
                    <option value="artificial">Artificial</option>
                    <option value="brass">Brass</option>
                    <option value="copper">Copper</option>
                    <option value="stainless-steel">Stainless Steel</option>
                  </select>
                </div>
              )}

              {/* Material - Only show for handy craft */}
              {formData.itemType === 'handycraft' && (
                <div>
                  <label htmlFor="material" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Material *
                  </label>
                  <select
                    id="material"
                    name="material"
                    required
                    value={formData.material}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Material</option>
                    <option value="wood">Wood</option>
                    <option value="clay">Clay</option>
                    <option value="fabric">Fabric</option>
                    <option value="paper">Paper</option>
                    <option value="metal">Metal</option>
                    <option value="glass">Glass</option>
                    <option value="plastic">Plastic</option>
                    <option value="stone">Stone</option>
                    <option value="leather">Leather</option>
                    <option value="bamboo">Bamboo</option>
                    <option value="ceramic">Ceramic</option>
                    <option value="resin">Resin</option>
                    <option value="mixed">Mixed Materials</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              )}

              {/* Metal Purity - Only show for precious metals */}
              {formData.itemType === 'jewellery' && ['gold', 'silver', 'platinum'].includes(formData.material) && (
                <div>
                  <label htmlFor="metalPurity" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                    Metal Purity (Optional)
                  </label>
                  <select
                    id="metalPurity"
                    name="metalPurity"
                    value={formData.metalPurity}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="">Select Purity</option>
                    {formData.material === 'gold' && (
                      <>
                        <option value="14k">14K</option>
                        <option value="18k">18K</option>
                        <option value="22k">22K</option>
                        <option value="24k">24K</option>
                      </>
                    )}
                    {formData.material === 'silver' && (
                      <option value="925-silver">925 Silver</option>
                    )}
                    <option value="not-applicable">Not Applicable</option>
                  </select>
                </div>
              )}

              {/* Description */}
              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium mb-2" style={{ color: '#222831' }}>
                  Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  required
                  rows="4"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="form-input w-full"
                  placeholder={formData.itemType === 'jewellery'
                    ? "Describe your jewellery - design, occasion, care instructions, authenticity, etc."
                    : "Describe your item in detail - style, fit, occasion, care instructions, etc."
                  }
                />
              </div>

              {/* Images */}
              <div className="md:col-span-2">
                <label htmlFor="images" className="block text-sm font-medium mb-3" style={{ color: '#222831' }}>
                  <i className="fas fa-images mr-2" style={{ color: '#948979' }}></i>
                  Product Images (Up to 3 photos) *
                </label>
                <div className="border-2 border-dashed rounded-2xl p-8 text-center transition-all hover:border-indigo-400 hover:bg-indigo-50/30" style={{ borderColor: imagePreviews.length > 0 ? '#948979' : '#DFD0B8' }}>
                  <input
                    type="file"
                    id="images"
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  
                  {imagePreviews.length === 0 ? (
                    <div
                      className="cursor-pointer py-4"
                      onClick={() => document.getElementById('images').click()}
                    >
                      <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #948979 0%, #DFD0B8 100%)' }}>
                        <i className="fas fa-cloud-upload-alt text-3xl text-white"></i>
                      </div>
                      <p className="text-lg font-semibold mb-2" style={{ color: '#222831' }}>
                        Click to upload images
                      </p>
                      <p className="text-sm mb-1" style={{ color: '#393E46' }}>
                        or drag and drop
                      </p>
                      <p className="text-xs mt-3 px-4 py-2 rounded-lg inline-block" style={{ color: '#948979', backgroundColor: 'rgba(148, 137, 121, 0.1)' }}>
                        <i className="fas fa-info-circle mr-1"></i>
                        PNG, JPG, GIF, WEBP up to 5MB each • Maximum 3 images
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        {imagePreviews.map((preview, index) => (
                          <div key={index} className="relative group">
                            <div className="relative rounded-xl overflow-hidden border-2 border-gray-200 group-hover:border-indigo-400 transition-all shadow-md hover:shadow-xl">
                              <img 
                                src={preview} 
                                alt={`Preview ${index + 1}`} 
                                className="w-full h-40 object-cover transition-transform duration-300 group-hover:scale-105" 
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="absolute bottom-2 left-2 text-white text-xs font-semibold bg-black/50 px-2 py-1 rounded">
                                  Image {index + 1}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-lg z-10"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                        {imagePreviews.length < 3 && (
                          <div 
                            className="h-40 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition-all group"
                            onClick={() => document.getElementById('images').click()}
                          >
                            <div className="text-center">
                              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-gray-100 group-hover:bg-indigo-100 flex items-center justify-center transition-colors">
                                <i className="fas fa-plus text-xl text-gray-400 group-hover:text-indigo-600 transition-colors"></i>
                              </div>
                              <p className="text-xs text-gray-500 group-hover:text-indigo-600 font-medium transition-colors">Add More</p>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between pt-2">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#948979' }}></div>
                          <span className="text-sm font-semibold" style={{ color: '#948979' }}>
                            {imagePreviews.length} / 3 images uploaded
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => document.getElementById('images').click()}
                          className="text-sm font-medium px-4 py-2 rounded-lg transition-all hover:shadow-md"
                          style={{ color: '#948979', backgroundColor: 'rgba(148, 137, 121, 0.1)' }}
                        >
                          <i className="fas fa-plus mr-2"></i>
                          {imagePreviews.length < 3 ? 'Add More Images' : 'Replace Images'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="text-sm" style={{ color: '#393E46' }}>
                <p className="flex items-center mb-1">
                  <i className="fas fa-info-circle mr-2" style={{ color: '#948979' }}></i>
                  Your listing will be active for 30 days
                </p>
                <p className="flex items-center">
                  <i className="fas fa-rocket mr-2 text-green-500"></i>
                  Your listing will be live immediately after submission
                </p>
              </div>
              <button
                type="submit"
                disabled={loading || !canCreateListing()}
                className="btn-primary flex items-center justify-center min-w-[200px]"
              >
                {loading ? (
                  <>
                    <i className="fas fa-circle-notch fa-spin mr-2"></i>Uploading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-plus mr-2"></i>List Item
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default ListItems;